import React, { useState, useEffect } from "react";
import { get2FAStatus, disable2FA, generateBackupCodes } from "../../functions/auth";
import { toast } from "react-toastify";
import Loading from "../../modal/Loading";

const TwoFactorAuth = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [showBackupForm, setShowBackupForm] = useState(false);
  const [password, setPassword] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);

  useEffect(() => {
    fetch2FAStatus();
  }, []);

  const fetch2FAStatus = async () => {
    setLoading(true);
    try {
      const response = await get2FAStatus();
      setStatus(response.data.data);
    } catch (error) {
      toast.error("Failed to fetch 2FA status");
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Please enter your password");
      return;
    }

    setLoading(true);
    try {
      await disable2FA(password);
      toast.success("2FA disabled successfully");
      setShowDisableForm(false);
      setPassword("");
      fetch2FAStatus();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to disable 2FA");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBackupCodes = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Please enter your password");
      return;
    }

    setLoading(true);
    try {
      const response = await generateBackupCodes(password);
      setBackupCodes(response.data.data.backupCodes);
      setShowBackupForm(false);
      setPassword("");
      fetch2FAStatus();
      toast.success("New backup codes generated");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to generate backup codes");
    } finally {
      setLoading(false);
    }
  };

  const copyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    toast.success("Backup codes copied to clipboard");
  };

  if (loading && !status) {
    return <Loading />;
  }

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">Two-Factor Authentication</h5>
      </div>
      <div className="card-body">
        <div className="row align-items-center mb-3">
          <div className="col">
            <h6 className="mb-1">Status</h6>
            <span className={`badge ${status?.twoFactorEnabled ? 'bg-success' : 'bg-secondary'}`}>
              {status?.twoFactorEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          <div className="col-auto">
            {status?.twoFactorEnabled && (
              <span className="text-muted">
                {status.backupCodesRemaining} backup codes remaining
              </span>
            )}
          </div>
        </div>

        {status?.twoFactorEnabled ? (
          <div>
            <p className="text-muted mb-3">
              Two-factor authentication is enabled for your account. This adds an extra layer of security.
            </p>
            
            <div className="d-flex gap-2 flex-wrap">
              {/* <button
                className="btn btn-outline-danger btn-sm"
                onClick={() => setShowDisableForm(true)}
              >
                Disable 2FA
              </button> */}
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => setShowBackupForm(true)}
              >
                Generate New Backup Codes
              </button>
            </div>

            {showDisableForm && (
              <div className="mt-3 p-3 border rounded">
                <h6>Disable Two-Factor Authentication</h6>
                <p className="text-muted small">
                  Enter your password to disable 2FA. This will make your account less secure.
                </p>
                <form onSubmit={handleDisable2FA}>
                  <div className="mb-2">
                    <input
                      type="password"
                      className="form-control"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      type="submit"
                      className="btn btn-danger btn-sm"
                      disabled={loading}
                    >
                      {loading ? "Disabling..." : "Disable 2FA"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        setShowDisableForm(false);
                        setPassword("");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {showBackupForm && (
              <div className="mt-3 p-3 border rounded">
                <h6>Generate New Backup Codes</h6>
                <p className="text-muted small">
                  Generate new backup codes. Your old codes will be invalidated.
                </p>
                <form onSubmit={handleGenerateBackupCodes}>
                  <div className="mb-2">
                    <input
                      type="password"
                      className="form-control"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm"
                      disabled={loading}
                    >
                      {loading ? "Generating..." : "Generate Codes"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        setShowBackupForm(false);
                        setPassword("");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {backupCodes.length > 0 && (
              <div className="mt-3 p-3 border rounded bg-light">
                <h6>Your New Backup Codes</h6>
                <p className="text-muted small mb-2">
                  Save these codes in a safe place. Each code can only be used once.
                </p>
                <div className="row">
                  {backupCodes.map((code, index) => (
                    <div key={index} className="col-md-6 mb-1">
                      <code className="bg-white p-1 rounded border">{code}</code>
                    </div>
                  ))}
                </div>
                <button
                  className="btn btn-outline-secondary btn-sm mt-2"
                  onClick={copyBackupCodes}
                >
                  Copy All Codes
                </button>
              </div>
            )}
          </div>
        ) : (
          <div>
            <p className="text-muted mb-3">
              Two-factor authentication is not enabled. Enable it to add an extra layer of security to your account.
            </p>
            <a href="/setup" className="btn btn-primary">
              Enable 2FA
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default TwoFactorAuth;

